import {
  ApiCommodityCommodityMarketingDetailResponse,
  apiCommodityJointStandardCommodityDetail,
  ApiCommodityJointStandardCommodityDetailResponse,
  apiCommodityJointStandardCommodityMarketingDetail,
  apiCommodityJointStandardSkuDetail,
  apiEvaluationEvaluateGetLabelList,
  ApiEvaluationEvaluateGetLabelListResponse,
  apiEvaluationEvaluateGetLatestEvaluateList,
  ApiEvaluationEvaluateGetLatestEvaluateListResponse,
  apiEvaluationEvaluateList,
} from '@/api'
import { AsyncReturnType, OmitStrict, RequiredDeep } from 'vtils/types'
import { createModelPlus, prepareData, submit } from '@/utils'
import { dcb } from '@dcb/jssdk'

export interface RawGoodsData
  extends RequiredDeep<ApiCommodityJointStandardCommodityDetailResponse> {
  /** 剩余库存 */
  surplusStock: number

  /** 购买数量 */
  buyCount: number

  /** 最小购买数量 */
  buyCountMin: number

  /** 最大购买数量 */
  buyCountMax: number

  /** 营销数据 */
  marketingData: RequiredDeep<ApiCommodityCommodityMarketingDetailResponse>

  /** 评论数据 */
  commentData: {
    /** 总评论 */
    total: number
    /** 好评率(99%) */
    goodRate: string
    /** 标签列表 */
    tags: RequiredDeep<ApiEvaluationEvaluateGetLabelListResponse>
    /** 最新评论列表 */
    latestComments: RequiredDeep<ApiEvaluationEvaluateGetLatestEvaluateListResponse>['list']
  }

  /** 是否已下架 */
  isOff: boolean

  /** 是否已售罄 */
  isSold: boolean

  /** 店铺是否休息中 */
  isRest: boolean

  /** 是否已停业 */
  isClosed: boolean

  /** SKU 选择器 */
  skuPicker: {
    /** 是否可见 */
    visible: boolean
    /** 操作 */
    action: 'addCart' | 'buy' | 'favorite' | 'groupBuying' | 'pure'
    /** 团购订单 ID */
    groupBuyingOrderId?: string
    /** 回调 */
    callback?: (goodData: GoodsData) => any
  }

  /** 分享参数 */
  shareParams: any

  /** 刷新营销 */
  refreshMarketing: () => Promise<void>

  /** 显示 SKU 选择器 */
  showSkuPicker: (payload: OmitStrict<this['skuPicker'], 'visible'>) => void

  /** 关闭 SKU 选择器 */
  closeSkuPicker: () => void

  /** 分享 */
  share: () => void
}

export type GoodsData = NonNullable<AsyncReturnType<typeof getGoodsData>>

/**
 * 获取商品数据。
 */
export async function getGoodsData({
  goodsId,
  simple,
}: {
  goodsId: string
  simple?: boolean
}) {
  // 获取商品原始数据
  const rawGoodsData: RequiredDeep<ApiCommodityJointStandardCommodityDetailResponse> | null =
    (await apiCommodityJointStandardCommodityDetail(
      {
        commodityId: goodsId,
      },
      {
        noErrorToast: true,
      },
    ).catch<null>(() => null)) as any

  if (!rawGoodsData) {
    return createModelPlus({
      isOff: true,
    } as RawGoodsData)
  }

  const stockData = await apiCommodityJointStandardSkuDetail({
    skuId: goodsId,
  })

  const goodsModel = createModelPlus<RawGoodsData>({
    ...{},
    ...rawGoodsData,
    isOff: false,
    skuPicker: {
      action: 'buy',
      visible: false,
    },
    get surplusStock() {
      return stockData.surplusStock!
    },
    buyCount: 0,
    get buyCountMin() {
      return Math.min(1, this.surplusStock)
    },
    get buyCountMax() {
      const 单日限量 = Math.min(
        this.singleDayLimitPurchases || this.surplusStock,
        this.surplusStock,
      )
      const 单用户限量 = Math.min(
        this.userDayLimitPurchases || 单日限量,
        单日限量,
      )
      return 单用户限量
    },
    marketingData: {} as any,
    commentData: {} as any,
    get isSold() {
      return !this.surplusStock
    },
    get isRest() {
      return rawGoodsData.storeStatus === 4
    },
    get shareParams() {
      return {
        title: this.commodityTitle,
        desc: this.commoditySubTitle,
        cover: this.coverPic,
        // 品牌升级商品才需要海报
        showImage: 0,
        paramsFree: {},
        resourceList: [
          {
            resourceId: this.commodityId as any,
            resourceName: this.commodityTitle as any,
          } as any,
        ],
      }
    },
    // 刷新营销
    async refreshMarketing() {
      await submit(async $ => {
        $.start()
        const {
          // 获取营销数据
          marketingData,
        } = await prepareData({
          marketingData: apiCommodityJointStandardCommodityMarketingDetail({
            commodityId: goodsId,
            skuId: goodsId,
          }),
        })
        this.marketingData = marketingData as any
      })
    },

    // 显示 SKU 选择器
    showSkuPicker(payload) {
      this.skuPicker = {
        ...payload,
        visible: true,
      }
    },

    // 关闭 SKU 选择器
    closeSkuPicker() {
      this.skuPicker = {
        action: 'buy',
        visible: false,
      }
    },

    share() {
      // 传参参考
      // https://yapi.gogpay.cn/project/2193/interface/api/47519
      dcb.share({
        ...this.shareParams,
        url: location.href,
      })
    },
  })

  const {
    // 营销数据
    marketingData,
    // 获取评论数据
    commentData,
  } = await prepareData({
    marketingData: apiCommodityJointStandardCommodityMarketingDetail({
      commodityId: goodsId,
      skuId: goodsId,
    }),
    commentData: simple
      ? ({
          total: 0,
          goodRate: '',
          latestComments: [],
          tags: [],
        } as RawGoodsData['commentData'])
      : Promise.all([
          // 获取评论总数
          apiEvaluationEvaluateList({
            commodityServiceId: goodsId,
            pageIndex: 1,
            pageSize: 1,
            havingPicFlag: 0,
          }),
          // 获取最新评论、好评率
          apiEvaluationEvaluateGetLatestEvaluateList({
            commodityServiceId: goodsId,
            pageIndex: 1,
            pageSize: 1,
          }),
          // 获取评论标签
          apiEvaluationEvaluateGetLabelList({
            commodityServiceId: goodsId,
          }),
        ]).then<RawGoodsData['commentData']>(
          ([totalSource, latestSource, tagSource]) => {
            return {
              total: totalSource.total,
              goodRate: latestSource.praiseRate as any,
              latestComments: latestSource.list as any,
              tags: tagSource as any,
            }
          },
        ),
  })

  goodsModel.marketingData = marketingData as any

  goodsModel.commentData = commentData as any

  return goodsModel
}
